import { ref, computed, useContext, inject, useRouter, useRoute } from '@nuxtjs/composition-api'
import { getEffectiveDatesRangeAsString } from '@/utils/effectiveDatesRange'

export default function useRevisions(type, groupId) {
  const { store, $axios } = useContext()
  const router = useRouter()
  const route = useRoute()
  const revisions = ref([])
  const selectedRevision = ref(null)
  const isLoading = ref(false)

  const rootRevision = inject('rootRevision', null)
  const currentRevision = inject('currentRevision', null)

  const school = computed(() => store.state.settings.school)
  const catalogId = computed(() => store.state.settings.activeCatalog)

  const enableHistoricalRevisions = computed(() =>
    store.state?.settings?.settings?.enableHistoricalRevisions || false
  )

  const historicalRevisionName = computed(() =>
    store.state?.settings?.settings?.historicalRevisionName || 'Historical Revisions'
  )

  const isSelectedRevisionRoot = computed(() => {
    if (!selectedRevision.value || !rootRevision) return false
    return selectedRevision.value.id === rootRevision.id
  })

  const shouldDisableInternalLinks = computed(() => {
    const isRootRevision = !route.value.query.effectiveDatesRange
    return enableHistoricalRevisions.value && !isRootRevision
  })

  const shouldHideAutoGeneratedCards = computed(() => {
    const isRootRevision = !route.value.query.effectiveDatesRange
    return enableHistoricalRevisions.value && !isRootRevision
  })

  const shouldAddEffectiveDatesRange = computed(() => {
    if (!selectedRevision.value) return false
    return (selectedRevision.value.effectiveStartDate || selectedRevision.value.effectiveEndDate) &&
      !isSelectedRevisionRoot.value
  })

  const fetchRevisions = async () => {
    if (!school.value || !catalogId.value || !groupId) {
      return []
    }

    isLoading.value = true

    try {
      const response = await $axios.$get(`/ca/${school.value}/catalogs/${catalogId.value}/${type}/${groupId}/revisions`)
      revisions.value = response || []

      // Initialize with current revision if available
      if (currentRevision?.id && revisions.value.length > 0) {
        const matchingRevision = revisions.value.find(
          rev => rev.id === currentRevision.id
        )
        if (matchingRevision) {
          selectRevision(matchingRevision, null, true)
        }
      }

      return response
    } catch (err) {
      console.error(`Failed to fetch ${type} revisions:`, err)
      revisions.value = []
      return []
    } finally {
      isLoading.value = false
    }
  }

  const selectRevision = (revision, closeModalCallback, replaceInsteadOfPush = false) => {
    selectedRevision.value = revision

    if (!revision) return

    const effectiveDatesRange = shouldAddEffectiveDatesRange.value
      ? getEffectiveDatesRangeAsString(revision)
      : ''

    const routerOptions = {
      path: `/${type}/${groupId}`,
      query: effectiveDatesRange ? { effectiveDatesRange } : {}
    }

    const toRoute = router.resolve(routerOptions)?.route?.fullPath
    const currentRoute = route.value?.fullPath

    if (toRoute !== currentRoute) {
      if (replaceInsteadOfPush) {
        router.replace(routerOptions)
      } else {
        router.push(routerOptions)
      }
    }

    if (closeModalCallback) {
      closeModalCallback()
    }
  }


  return {
    revisions,
    selectedRevision,
    fetchRevisions,
    selectRevision,
    enableHistoricalRevisions,
    historicalRevisionName,
    isLoading,
    isSelectedRevisionRoot,
    rootRevision,
    shouldDisableInternalLinks,
    shouldHideAutoGeneratedCards,
  }
}
